import { useState } from "react";
import {
  Volume2, Loader2, Languages, CheckCircle2, ListChecks, Image as ImageIcon,
  BookOpen, PenLine, Mic, Repeat, Blocks, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { fileUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const TYPE_META = {
  translation: { icon: Languages, color: "text-primary" },
  pronunciation: { icon: Mic, color: "text-secondary" },
  shadowing: { icon: Repeat, color: "text-secondary" },
  listen_select: { icon: ListChecks, color: "text-primary" },
  image_sentence: { icon: ImageIcon, color: "text-secondary" },
  reading_repetition: { icon: BookOpen, color: "text-secondary" },
  guided_writing: { icon: PenLine, color: "text-primary" },
  short_quiz: { icon: ListChecks, color: "text-secondary" },
  sentence_building: { icon: Blocks, color: "text-primary" },
};

// Voice-based exercises: the AI evaluation UI is scaffolded but disabled for now.
const VOICE_TYPES = ["pronunciation", "shadowing", "reading_repetition"];

function GradeBadge({ grade }) {
  const { t } = useI18n();
  if (!grade) return null;
  const tone = grade === "excellent" || grade === "good_job" ? "bg-emerald-100 text-emerald-700"
    : grade === "great_effort" ? "bg-primary/15 text-primary"
    : grade === "getting_better" ? "bg-secondary/25 text-secondary-foreground"
    : "bg-destructive/15 text-destructive";
  return <span className={`inline-block rounded-full px-3 py-1 text-sm font-bold ${tone}`}>{t(`grade.${grade}`)}</span>;
}

export default function ExercisePlayer({ exercise }) {
  const { t, lang } = useI18n();
  const cfg = exercise.config || {};
  const meta = TYPE_META[exercise.type] || TYPE_META.translation;
  const Icon = meta.icon;

  const [text, setText] = useState("");
  const [selected, setSelected] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [chosen, setChosen] = useState([]); // sentence_building word indices
  const [loading, setLoading] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const playTts = async (phrase) => {
    if (!phrase) return;
    setTtsLoading(true);
    try {
      const res = await api.get(`/tts`, { params: { text: phrase }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      new Audio(url).play();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "TTS error");
    } finally {
      setTtsLoading(false);
    }
  };

  const handleResult = (data) => {
    setResult(data);
    if (data.grade) toast.success(t(`grade.${data.grade}`));
  };

  const submitText = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/exercises/${exercise.id}/submit-text`, { response: text });
      handleResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitChoice = async (value) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/exercises/${exercise.id}/submit-choice`, { selected: value });
      handleResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const bank = cfg.words || [];
  const available = bank.map((w, i) => ({ w, i })).filter(({ i }) => !chosen.includes(i));

  return (
    <div className="rounded-xl border border-border p-4 sm:p-5 bg-card" data-testid={`exercise-player-${exercise.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${meta.color}`} />
        <Badge variant="outline" className="rounded-full">{t(`ex.${exercise.type}`)}</Badge>
      </div>
      {exercise.prompt && <p className="font-semibold mb-3">{exercise.prompt}</p>}

      {/* ---- Translation ---- */}
      {exercise.type === "translation" && (
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/50 p-3 text-lg font-bold">{cfg.source_text}</div>
          <Textarea data-testid="exercise-text-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={t("student.translate_here")} className="rounded-xl" />
          <Button onClick={submitText} disabled={loading || !text} className="rounded-full" data-testid="exercise-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("student.submit_answer")}
          </Button>
        </div>
      )}

      {exercise.type === "guided_writing" && (
        <div className="space-y-3">
          {cfg.guidance && <p className="text-sm text-muted-foreground">💡 {cfg.guidance}</p>}
          <Textarea data-testid="exercise-text-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={t("student.write_here")} className="rounded-xl" />
          <Button onClick={submitText} disabled={loading || !text} className="rounded-full" data-testid="exercise-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("student.submit_answer")}
          </Button>
        </div>
      )}

      {exercise.type === "image_sentence" && (
        <div className="space-y-3">
          {cfg.image_url && <img src={fileUrl(cfg.image_url)} alt="exercise" className="rounded-xl border border-border max-h-60 object-cover" />}
          <Textarea data-testid="exercise-text-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={t("student.write_here")} className="rounded-xl" />
          <Button onClick={submitText} disabled={loading || !text} className="rounded-full" data-testid="exercise-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("student.submit_answer")}
          </Button>
        </div>
      )}

      {/* ---- Sentence building ---- */}
      {exercise.type === "sentence_building" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t("student.tap_words")}</p>
          <div className="min-h-[3rem] rounded-xl border border-dashed border-border p-3 flex flex-wrap gap-2 bg-muted/30">
            {chosen.length === 0 && <span className="text-sm text-muted-foreground">…</span>}
            {chosen.map((idx, pos) => (
              <button key={pos} data-testid={`sb-chosen-${pos}`} onClick={() => setChosen(chosen.filter((_, p) => p !== pos))}
                className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold hover:opacity-90">
                {bank[idx]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {available.map(({ w, i }) => (
              <button key={i} data-testid={`sb-word-${i}`} onClick={() => setChosen([...chosen, i])}
                className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-semibold hover:bg-muted">
                {w}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => submitChoice(chosen.map((i) => bank[i]))} disabled={loading || chosen.length === 0} className="rounded-full" data-testid="exercise-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("student.submit_answer")}
            </Button>
            {chosen.length > 0 && <Button variant="ghost" onClick={() => setChosen([])} className="rounded-full">{t("student.clear")}</Button>}
          </div>
        </div>
      )}

      {/* ---- Voice exercises (UI ready, AI evaluation disabled for now) ---- */}
      {VOICE_TYPES.includes(exercise.type) && (
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/50 p-3 text-lg font-bold flex items-center justify-between gap-2">
            <span>{cfg.target_phrase || cfg.text}</span>
            <Button size="sm" variant="outline" className="rounded-full shrink-0" data-testid="exercise-listen-button"
              onClick={() => playTts(cfg.target_phrase || cfg.text)} disabled={ttsLoading}>
              {ttsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
            </Button>
          </div>
          <div className="rounded-xl border border-dashed border-secondary/40 bg-secondary/5 p-4 flex items-center gap-3" data-testid="voice-coming-soon">
            <span className="h-10 w-10 rounded-full bg-secondary/15 flex items-center justify-center shrink-0"><Mic className="h-5 w-5 text-secondary" /></span>
            <div>
              <Badge variant="outline" className="rounded-full mb-1"><Clock className="h-3 w-3 mr-1" />{t("ex.coming_soon")}</Badge>
              <p className="text-sm text-muted-foreground">{t("student.voice_disabled")}</p>
            </div>
          </div>
          <Button disabled className="rounded-full opacity-60" data-testid="exercise-record-disabled">
            <Mic className="h-4 w-4 mr-1" />{t("common.record")}
          </Button>
        </div>
      )}

      {/* ---- Listen & select ---- */}
      {exercise.type === "listen_select" && (
        <div className="space-y-3">
          <Button variant="outline" className="rounded-full" data-testid="exercise-listen-button"
            onClick={() => playTts(cfg.phrase)} disabled={ttsLoading}>
            {ttsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Volume2 className="h-4 w-4 mr-1" />} {t("common.listen")}
          </Button>
          <p className="text-sm text-muted-foreground">{t("student.select_one")}</p>
          <div className="grid gap-2">
            {(cfg.options || []).map((opt, i) => (
              <button key={i} data-testid={`listen-option-${i}`}
                onClick={() => { setSelected(i); submitChoice(i); }}
                disabled={loading}
                className={`text-left rounded-xl border px-4 py-3 transition-colors ${selected === i ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- Short quiz ---- */}
      {exercise.type === "short_quiz" && (
        <div className="space-y-4">
          {(cfg.questions || []).map((q, qi) => (
            <div key={qi} className="rounded-lg border border-border p-3">
              <p className="font-semibold mb-2">{qi + 1}. {q.question}</p>
              <div className="grid gap-2">
                {(q.options || []).map((opt, oi) => (
                  <button key={oi} data-testid={`quiz-${qi}-option-${oi}`}
                    onClick={() => setQuizAnswers({ ...quizAnswers, [qi]: oi })}
                    className={`text-left rounded-lg border px-3 py-2 text-sm transition-colors ${quizAnswers[qi] === oi ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <Button data-testid="exercise-submit-quiz-button"
            onClick={() => submitChoice((cfg.questions || []).map((_, i) => (quizAnswers[i] ?? -1)))}
            disabled={loading} className="rounded-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("student.submit_answer")}
          </Button>
        </div>
      )}

      {/* ---- Result ---- */}
      {result && (
        <div className="mt-4 rounded-xl bg-muted/40 border border-border p-4 space-y-2" data-testid="exercise-result">
          <div className="flex items-center gap-3 flex-wrap">
            <GradeBadge grade={result.grade} />
            {typeof result.score === "number" && (
              <span className="text-sm text-muted-foreground">{t("student.score")}: <b>{result.score}/100</b></span>
            )}
          </div>
          {result.transcript ? (
            <p className="text-sm"><span className="text-muted-foreground">{t("student.heard")}:</span> “{result.transcript}”</p>
          ) : null}
          <p className="text-sm flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>{lang === "es" ? result.feedback_es : (result.feedback_en || result.feedback_es)}</span>
          </p>
          {result.correct_version ? (
            <p className="text-sm text-muted-foreground">{t("student.correct_answer")}: <b className="text-foreground">{result.correct_version}</b></p>
          ) : null}
        </div>
      )}
    </div>
  );
}
